import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, useMediaQuery, useTheme, Grid, Divider } from '@mui/material';
import { useLocation } from 'react-router-dom';
import WhiteboardToolbar from './whiteboard/WhiteboardToolbar';
import SaveWhiteboardModal from './whiteboard/save-whiteboard-modal';
import LoadWhiteboardModal from './whiteboard/load-whiteboard-modal';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import PartnerApi from '../../../rest-api/partner-api';
import DisplayHandoutEmbed from './display-handout-embed';
import AuthorsAndReferences from './authors-and-references';
import HandoutActions from './handout-actions';

function HandoutViewerEmbed() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const ids = queryParams.get('ids');
    const token = queryParams.get('token');
    // console.warn('[HandoutViewerEmbed] Query param "token":', token);
    // console.warn('[HandoutViewerEmbed] Query param "ids":', ids);
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [handouts, setHandouts] = useState(null);
    const [clinicData, setClinicData] = useState(null);
    const canvasRef = useRef(null);

    const [showWhiteboard, setShowWhiteboard] = useState(false);
    const [show3DModel, setShow3DModel] = useState(true);
    const [showMainIllustration, setShowMainIllustration] = useState(true);
    const [showTutorialVideo, setShowTutorialVideo] = useState(false);
    const [showAuthorsAndReferences, setShowAuthorsAndReferences] = useState(false);

    const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);
    const [activeTool, setActiveTool] = useState('pen');
    const [color, setColor] = useState('#000000');
    const [thickness, setThickness] = useState(2);

    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);

    const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges();

    useEffect(() => {
        const fetchPartnerHandouts = async () => {
            try {
                const response = await PartnerApi.getHandoutContent(token, ids);

                // response is an array of handouts when embedded app
                const handouts = response;
                setHandouts(handouts);

                /* extra call – owns the logo / email */
                const meta = await PartnerApi.getUserAndLogo(token);
                setClinicData(meta);
            } catch (err) {
                console.error('Failed to fetch handout:', err);
            }
        };
        fetchPartnerHandouts();
    }, [ids]);

    const handleMainIllustrationToggle = (e) => setShowMainIllustration(e.target.checked);
    const toggleAuthorsAndReferences = () => setShowAuthorsAndReferences((prev) => !prev);
    const handleToggleWhiteboard = (_, checked) => setShowWhiteboard(checked);
    const handleSave = () => setIsSaveModalOpen(true);
    const handleLoad = () => setIsLoadModalOpen(true);
    const openLoadModal = () => setIsLoadModalOpen(true);
    const handleReset = () => {
        if (canvasRef.current) {
            canvasRef.current.clear();
            localStorage.removeItem('lastWhiteboardState');
            setHasUnsavedChanges(false);
        }
    };
    const handleWhiteboardSaved = () => {
        setHasUnsavedChanges(false);
        setIsSaveModalOpen(false);
    };

    if (!handouts) {
        return (
            <Box sx={{ my: 2 }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    const allowAuthorsButton = handouts.length === 1;

    return (
        <>
            <HandoutActions
                handoutData={handouts[0]} // or loop if multiple
                onToggleAuthorsAndReferences={() => setShowAuthorsAndReferences((v) => !v)}
                embedMode={true}
                allowAuthorsButton={allowAuthorsButton}
            />
            <Box sx={{ px: { xs: 2, sm: 3, md: 3 } }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={9}>
                        {handouts.map((handout, index) =>
                            <DisplayHandoutEmbed
                                key={index}
                                handoutData={handout}
                                showMainIllustration={showMainIllustration}
                                show3DModel={show3DModel}
                                showTutorialVideo={showTutorialVideo}
                                handleMainIllustrationToggle={handleMainIllustrationToggle}
                                showWhiteboard={showWhiteboard}
                                handleToggleWhiteboard={handleToggleWhiteboard}
                                token={token}
                                clinicData={clinicData}
                            />,
                        )}
                    </Grid>

                    <Grid item xs={12} md={3}>
                        {showWhiteboard && (
                            <>
                                <WhiteboardToolbar
                                    activeTool={activeTool}
                                    setActiveTool={setActiveTool}
                                    color={color}
                                    setColor={setColor}
                                    thickness={thickness}
                                    setThickness={setThickness}
                                    isToolbarExpanded={isToolbarExpanded}
                                    setIsToolbarExpanded={setIsToolbarExpanded}
                                    handleSave={handleSave}
                                    openLoadModal={openLoadModal}
                                    handleReset={handleReset}
                                />
                            </>
                        )}
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{ my: 3 }} />
            <SaveWhiteboardModal
                open={isSaveModalOpen}
                onClose={() => setIsSaveModalOpen(false)}
                canvasRef={canvasRef}
                onSaveComplete={handleWhiteboardSaved}
            />
            <LoadWhiteboardModal
                open={isLoadModalOpen}
                onClose={() => setIsLoadModalOpen(false)}
                canvasRef={canvasRef}
            />
            <AuthorsAndReferences
                handoutData={handouts[0]}
                open={showAuthorsAndReferences}
                onToggleAuthorsAndReferences={toggleAuthorsAndReferences}
            />
        </>
    );
}

export default HandoutViewerEmbed;
